import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ShoppingBagIcon,
  CubeIcon,
  UsersIcon,
  StarIcon,
  BanknotesIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { getApiUrl } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { AreaChart, BarChart, BreakdownBar, fullMonth, shortMonth } from '../../components/admin/Charts';

const RANGES = [7, 14, 30];

const STATUS_COLORS = {
  'Order Placed': '#f59e0b',
  Packed: '#6366f1',
  Shipped: '#0ea5e9',
  'Out for Delivery': '#8b5cf6',
  Delivered: '#16a34a',
  Cancelled: '#ef4444'
};

const count = (value) => Number(value || 0).toLocaleString('en-IN');
const rupees = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const StatCard = ({ icon: Icon, label, value, hint, accent }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-gray-500">{label}</p>
        <p className="mt-1 text-2xl sm:text-3xl font-bold text-gray-900 truncate">{value}</p>
        {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
      </div>
      <span className="shrink-0 rounded-lg p-2" style={{ backgroundColor: `${accent}1a`, color: accent }}>
        <Icon className="h-6 w-6" />
      </span>
    </div>
  </div>
);

const Panel = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5">
    <div className="mb-3">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      {subtitle ? <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p> : null}
    </div>
    {children}
  </div>
);

const HomeData = () => {
  const { token } = useAuth();
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(getApiUrl(`/api/admin/overview?days=${days}`), {
        headers: { 'x-admin': token, Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, [days, token]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const series = useMemo(() => {
    const daily = data?.daily || [];
    return {
      orders: daily.map((d) => ({ date: d.date, value: d.orders })),
      revenue: daily.map((d) => ({ date: d.date, value: d.revenue })),
      customers: daily.map((d) => ({ date: d.date, value: d.customers })),
      reviews: daily.map((d) => ({ date: d.date, value: d.reviews })),
      monthlyOrders: (data?.monthly || []).map((m) => ({ date: `${m.month}-01`, value: m.orders })),
      monthlyRevenue: (data?.monthly || []).map((m) => ({ date: `${m.month}-01`, value: m.revenue }))
    };
  }, [data]);

  const totals = data?.totals;
  const period = data?.periodTotals;
  const today = data?.today;
  const statusTotal = (data?.ordersByStatus || []).reduce((sum, row) => sum + row.count, 0);
  const topQuantity = Math.max(...(data?.topProducts || []).map((p) => p.quantity), 1);
  const quietPeriod = data && !period.orders && !period.customers && data.lastOrderAt;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Store totals and day-by-day activity{data ? ` · last ${data.range.days} days (IST)` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            {RANGES.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setDays(option)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  days === option ? 'bg-pink-50 text-pink-600 font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {option}d
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={fetchOverview}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {loading && !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-28 rounded-xl bg-white border border-gray-100 animate-pulse" />
          ))}
        </div>
      ) : null}

      {data ? (
        <div className="space-y-4 sm:space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            <StatCard
              icon={ShoppingBagIcon}
              label="Total orders"
              value={count(totals.orders)}
              hint={`${count(today.orders)} today · ${count(period.orders)} in ${data.range.days}d`}
              accent="#db2777"
            />
            <StatCard
              icon={CubeIcon}
              label="Total products"
              value={count(totals.products)}
              hint={`${count(totals.activeProducts)} active · ${count(totals.outOfStockProducts)} out of stock`}
              accent="#6366f1"
            />
            <StatCard
              icon={UsersIcon}
              label="Total customers"
              value={count(totals.customers)}
              hint={`${count(period.customers)} joined in ${data.range.days}d`}
              accent="#0ea5e9"
            />
            <StatCard
              icon={StarIcon}
              label="Reviews added"
              value={count(totals.reviews)}
              hint={`${count(totals.productReviews)} product · ${count(totals.googleReviews)} Google`}
              accent="#f59e0b"
            />
            <StatCard
              icon={BanknotesIcon}
              label="Revenue"
              value={rupees(totals.revenue)}
              hint={`${rupees(totals.avgOrderValue)} avg order`}
              accent="#16a34a"
            />
          </div>

          {quietPeriod ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              No orders or sign-ups in the last {data.range.days} days. The last order came in on{' '}
              {new Date(data.lastOrderAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })},
              so the daily charts below are flat — the monthly trend shows the longer picture.
            </div>
          ) : null}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
            <Panel title="Orders per day" subtitle={`${count(period.orders)} orders in this period`}>
              <BarChart data={series.orders} color="#db2777" />
            </Panel>
            <Panel title="Revenue per day" subtitle={`${rupees(period.revenue)} in this period · cancelled orders excluded`}>
              <AreaChart data={series.revenue} color="#16a34a" formatValue={(v) => Math.round(v).toLocaleString('en-IN')} unit="₹" />
            </Panel>
            <Panel title="New customers per day" subtitle={`${count(period.customers)} sign-ups in this period`}>
              <BarChart data={series.customers} color="#0ea5e9" />
            </Panel>
            <Panel title="Reviews per day" subtitle={`${count(period.reviews)} reviews in this period`}>
              <BarChart data={series.reviews} color="#f59e0b" />
            </Panel>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
            <Panel title="Orders by month" subtitle="Last 12 months">
              <BarChart
                data={series.monthlyOrders}
                color="#6366f1"
                formatLabel={shortMonth}
                formatTooltip={fullMonth}
              />
            </Panel>
            <Panel title="Revenue by month" subtitle="Last 12 months · cancelled orders excluded">
              <AreaChart
                data={series.monthlyRevenue}
                color="#801c33"
                formatValue={(v) => Math.round(v).toLocaleString('en-IN')}
                unit="₹"
                formatLabel={shortMonth}
                formatTooltip={fullMonth}
              />
            </Panel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
            <Panel title="Orders by status" subtitle="All time">
              {data.ordersByStatus.length ? (
                <div className="space-y-3">
                  {data.ordersByStatus.map((row) => (
                    <BreakdownBar
                      key={row.status}
                      label={row.status}
                      value={row.count}
                      total={statusTotal}
                      hint={`${statusTotal ? Math.round((row.count / statusTotal) * 100) : 0}%`}
                      color={STATUS_COLORS[row.status] || '#9ca3af'}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No orders yet.</p>
              )}
            </Panel>
            <Panel title="Top selling products" subtitle="By pieces sold, all time">
              {data.topProducts.length ? (
                <div className="space-y-3">
                  {data.topProducts.map((product) => (
                    <BreakdownBar
                      key={product.name}
                      label={product.name}
                      value={product.quantity}
                      total={topQuantity}
                      hint={rupees(product.revenue)}
                      color="#801c33"
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No sales yet.</p>
              )}
            </Panel>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default HomeData;
